import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip as MuiTooltip,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer as BarResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from 'recharts';
import styles from './po_workflow_management.module.css';
import { getPoWorkflowDashboard } from './functions/get_po_workflow_dashboard.function';

/**
 * @param {Object} props
 * @param {() => Promise<{ invoices?: Array }>} [props.fetchDashboard]
 */
import * as echarts from 'echarts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PIE_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#F97316',
  '#84CC16',
];

const STATUS_COLORS = {
  pending: '#F59E0B',
  approved: '#10B981',
  completed: '#10B981',
  rejected: '#EF4444',
  pending_rejection: '#F97316',
  stop: '#3B82F6',
  reverted: '#8B5CF6',
};

const REMAINING_COLOR = '#0E7490';

const normalizeStatusKey = (status) =>
  String(status || '')
    .trim()
    .toLowerCase();

const getStatusColor = (status) => STATUS_COLORS[normalizeStatusKey(status)] || '#94A3B8';

const INVOICE_TYPE_OPTIONS = [
  { value: 'FTTS', label: 'FTTS' },
  { value: 'NON_FTTS', label: 'Non-FTTS' },
  { value: 'OTHER', label: 'Other' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

const INVOICE_TYPE_COLORS = {
  FTTS: '#2563EB',
  NON_FTTS: '#EA580C',
  OTHER: '#7C3AED',
  UNKNOWN: '#475569',
};

const INVOICE_TYPE_GRADIENTS = {
  FTTS: 'url(#invoiceTypeFttsGradient)',
  NON_FTTS: 'url(#invoiceTypeNonFttsGradient)',
  OTHER: 'url(#invoiceTypeOtherGradient)',
  UNKNOWN: 'url(#invoiceTypeUnknownGradient)',
};

const INVOICE_TYPE_CHART_META = [
  { key: 'FTTS', label: 'FTTS' },
  { key: 'NON_FTTS', label: 'Non-FTTS' },
  { key: 'OTHER', label: 'Other' },
  { key: 'UNKNOWN', label: 'Unknown' },
];

const resolveInvoiceTypeKey = (invoice) => {
  const raw = invoice?.invoice_type ?? invoice?.invoiceType ?? '';
  const normalized = String(raw).trim().toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');

  if (!normalized) {
    return 'UNKNOWN';
  }

  if (normalized === 'FTTS') {
    return 'FTTS';
  }
  if (normalized === 'NON_FTTS' || normalized === 'NONFTTS') {
    return 'NON_FTTS';
  }

  const invoiceNumber = String(invoice?.invoice_number || '')
    .trim()
    .toUpperCase();
  if (
    invoiceNumber.startsWith('NONFTTS') ||
    invoiceNumber.includes('NON_FTTS') ||
    invoiceNumber.includes('NON-FTTS')
  ) {
    return 'NON_FTTS';
  }
  if (invoiceNumber.startsWith('FTTS') || invoiceNumber.includes('FTTS')) {
    return 'FTTS';
  }

  return 'OTHER';
};

const getInvoiceTypeLabel = (key) => {
  const match = INVOICE_TYPE_CHART_META.find((item) => item.key === key);
  return match?.label || 'Unknown';
};

const DARK_FIELD_GRADIENT =
  'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%), linear-gradient(135deg, rgba(91, 10, 28, 0.9) 0%, rgba(49, 7, 41, 0.96) 100%)';

const getSearchFieldSx = (isDark, hasValue) => {
  if (!isDark) {
    return {
      width: '300px',
      backgroundColor: hasValue ? '#f0f9ff' : '#fff',
      '& .MuiInputBase-input': {
        color: hasValue ? '#0369a1' : 'gray',
        '::placeholder': {
          color: '#b3b9c4',
          opacity: 1,
        },
      },
      '& .MuiOutlinedInput-root': {
        border: hasValue ? '1px solid #7dd3fc' : '1px solid #ccc',
        borderRadius: '10px',
        paddingRight: 0,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    };
  }

  return {
    width: '300px',
    background: DARK_FIELD_GRADIENT,
    boxShadow: hasValue ? '0 10px 28px rgba(91, 10, 28, 0.34)' : '0 8px 22px rgba(49, 7, 41, 0.24)',
    '& .MuiInputBase-input': {
      color: hasValue ? '#ffd5de' : '#fff0f4',
      fontWeight: hasValue ? 600 : 500,
      '::placeholder': {
        color: 'rgba(255, 220, 230, 0.68)',
        opacity: 1,
      },
    },
    '& .MuiOutlinedInput-root': {
      border: hasValue
        ? '1px solid rgba(255, 182, 198, 0.62)'
        : '1px solid rgba(255, 224, 232, 0.32)',
      borderRadius: '10px',
      paddingRight: 0,
      '&:hover': {
        border: '1px solid rgba(255, 196, 210, 0.5)',
      },
      '&.Mui-focused': {
        border: '1px solid rgba(255, 182, 198, 0.72)',
        boxShadow: '0 0 0 3px rgba(255, 182, 198, 0.14)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  };
};

const getSelectFieldSx = (isDark, isSelected, accent = {}) => {
  const {
    lightBg = '#fff',
    lightSelectedBg = '#eff6ff',
    lightBorder = '#ccc',
    lightSelectedBorder = '#93c5fd',
    lightColor = '#757575',
    lightSelectedColor = '#1d4ed8',
    darkSelectedBorder = 'rgba(255, 182, 198, 0.62)',
    darkSelectedColor = '#ffd5de',
  } = accent;

  if (!isDark) {
    return {
      backgroundColor: isSelected ? lightSelectedBg : lightBg,
      border: isSelected ? `1px solid ${lightSelectedBorder}` : `1px solid ${lightBorder}`,
      color: isSelected ? lightSelectedColor : lightColor,
      borderRadius: '10px',
      '& .MuiSelect-select': {
        padding: '8px 12px',
      },
      '&:hover': {
        border: isSelected ? `1px solid ${lightSelectedBorder}` : `1px solid ${lightBorder}`,
      },
      '&.Mui-focused': {
        border: isSelected ? `1px solid ${lightSelectedBorder}` : `1px solid ${lightBorder}`,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    };
  }

  return {
    background: DARK_FIELD_GRADIENT,
    border: isSelected ? `1px solid ${darkSelectedBorder}` : '1px solid rgba(255, 224, 232, 0.32)',
    color: isSelected ? darkSelectedColor : 'rgba(255, 240, 244, 0.9)',
    borderRadius: '10px',
    boxShadow: isSelected
      ? '0 10px 28px rgba(91, 10, 28, 0.34)'
      : '0 8px 22px rgba(49, 7, 41, 0.24)',
    fontWeight: isSelected ? 600 : 500,
    '& .MuiSelect-select': {
      padding: '8px 12px',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 220, 230, 0.78)',
    },
    '&:hover': {
      border: isSelected ? `1px solid ${darkSelectedBorder}` : '1px solid rgba(255, 196, 210, 0.5)',
    },
    '&.Mui-focused': {
      border: `1px solid ${darkSelectedBorder}`,
      boxShadow: '0 0 0 3px rgba(255, 182, 198, 0.14)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  };
};

const getFilterChipSx = (isDark, accent) => {
  const { bg, border, color } = accent;
  if (!isDark) {
    return {
      backgroundColor: bg,
      border: `1px solid ${border}`,
      color,
      '& .MuiChip-deleteIcon': { color },
    };
  }

  return {
    background:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.04) 100%), linear-gradient(135deg, rgba(91, 10, 28, 0.88) 0%, rgba(49, 7, 41, 0.94) 100%)',
    border: `1px solid ${border}`,
    color: '#fff0f4',
    boxShadow: `0 8px 22px rgba(49, 7, 41, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.08)`,
    fontWeight: 600,
    '& .MuiChip-deleteIcon': { color: '#ffd5de' },
  };
};

const getIconButtonSx = (isDark, options = {}) => {
  const { active = false, disabled = false } = options;
  if (!isDark) {
    return {
      border: '1px solid #d0d5dd',
      borderRadius: '10px',
      color: active ? '#fff' : '#667085',
      background: active ? 'linear-gradient(135deg, #5b0a1c 0%, #310729 100%)' : '#fff',
      '&:hover': {
        backgroundColor: active ? '#5b0a1c' : '#f9fafb',
        borderColor: '#98a2b3',
      },
      '&.Mui-disabled': {
        borderColor: '#eaecf0',
        color: '#d0d5dd',
      },
    };
  }

  if (disabled) {
    return {
      border: '1px solid rgba(255, 224, 232, 0.14)',
      borderRadius: '10px',
      color: 'rgba(255, 220, 230, 0.28)',
      background: 'rgba(49, 7, 41, 0.35)',
    };
  }

  return {
    border: active ? '1px solid rgba(255, 182, 198, 0.62)' : '1px solid rgba(255, 224, 232, 0.32)',
    borderRadius: '10px',
    color: '#fff0f4',
    background: active ? 'linear-gradient(135deg, #5b0a1c 0%, #310729 100%)' : DARK_FIELD_GRADIENT,
    boxShadow: active ? '0 10px 28px rgba(91, 10, 28, 0.34)' : '0 8px 22px rgba(49, 7, 41, 0.24)',
    '&:hover': {
      borderColor: 'rgba(255, 196, 210, 0.55)',
      backgroundColor: 'rgba(91, 10, 28, 0.92)',
    },
  };
};

const CustomAmountTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles['custom-tooltip']}>
        <div className={styles['tooltip-label']}>{label}</div>
        <div className={styles['tooltip-value']}>Rs {payload[0].value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

const CustomCountTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles['custom-tooltip']}>
        <div className={styles['tooltip-label']}>{label}</div>
        <div className={styles['tooltip-value']}>{payload[0].value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

const InvoiceTypeChartTooltip = ({ active, payload, total }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const entry = payload[0];
  const value = Number(entry.value || 0);
  const percent = total ? ((value * 100) / total).toFixed(1) : '0.0';
  const color = entry.payload?.color || '#2563EB';

  return (
    <div className={styles['invoice-type-tooltip']}>
      <div className={styles['invoice-type-tooltip-header']}>
        <span className={styles['invoice-type-tooltip-dot']} style={{ backgroundColor: color }} />
        <span>{entry.name}</span>
      </div>
      <div className={styles['invoice-type-tooltip-row']}>
        <span>Count</span>
        <strong>{value.toLocaleString()}</strong>
      </div>
      <div className={styles['invoice-type-tooltip-row']}>
        <span>Share</span>
        <strong>{percent}%</strong>
      </div>
    </div>
  );
};

export const DashboardTab = ({
  fetchDashboard: fetchDashboardData = getPoWorkflowDashboard,
  showPoSummary = true,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPO, setSelectedPO] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [topPoLimit, setTopPoLimit] = useState(10);
  const [activeInvoiceTypeIndex, setActiveInvoiceTypeIndex] = useState(null);
  const jitterScatterChartRef = useRef(null);
  const dashboardRef = useRef(null);

  // Always use the dark dashboard background (no light/white toggle)
  const isDarkBackground = true;
  const chartTextColor = '#f8dfe6';
  const chartSubtleTextColor = 'rgba(255, 233, 239, 0.78)';
  const chartAxisColor = 'rgba(255, 220, 230, 0.48)';
  const chartGridColor = 'rgba(255, 220, 230, 0.12)';

  useEffect(() => {
    let cancelled = false;
    const loadDashboard = async () => {
      setDashboardLoading(true);
      try {
        const data = await fetchDashboardData();
        if (!cancelled) {
          setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
        }
      } finally {
        if (!cancelled) {
          setDashboardLoading(false);
        }
      }
    };
    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [fetchDashboardData]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      window.dispatchEvent(new Event('resize'));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!dashboardRef.current) {
      return;
    }

    try {
      if (!document.fullscreenElement) {
        await dashboardRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  };

  const poOptions = useMemo(
    () =>
      [...new Set(invoices.map((i) => String(i?.po_number || '').trim()).filter(Boolean))].sort(),
    [invoices]
  );
  const vendorOptions = useMemo(
    () => [...new Set(invoices.map((i) => String(i?.vendor || '').trim()).filter(Boolean))].sort(),
    [invoices]
  );
  const yearOptions = useMemo(() => {
    const years = [
      ...new Set(
        invoices
          .map((i) => new Date(i?.created_at).getFullYear())
          .filter((year) => Number.isFinite(year))
          .map(String)
      ),
    ].sort((a, b) => Number(b) - Number(a));
    return years;
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const po = String(inv?.po_number || '').trim();
      const vendor = String(inv?.vendor || '').trim();
      const status = String(inv?.status || '')
        .trim()
        .toLowerCase();
      const invoiceNumber = String(inv?.invoice_number || '').trim();
      const createdAt = inv?.created_at ? new Date(inv.created_at) : null;
      const year =
        createdAt && Number.isFinite(createdAt.getTime()) ? String(createdAt.getFullYear()) : '';
      const invoiceTypeKey = resolveInvoiceTypeKey(inv);
      const term = searchTerm.trim().toLowerCase();

      const passSearch =
        !term ||
        po.toLowerCase().includes(term) ||
        vendor.toLowerCase().includes(term) ||
        status.includes(term) ||
        invoiceNumber.toLowerCase().includes(term) ||
        getInvoiceTypeLabel(invoiceTypeKey).toLowerCase().includes(term);
      const passPo = !selectedPO || po === selectedPO;
      const passVendor = !selectedVendor || vendor === selectedVendor;
      const passYear = !selectedYear || year === selectedYear;
      const passInvoiceType = !selectedInvoiceType || invoiceTypeKey === selectedInvoiceType;

      return passSearch && passPo && passVendor && passYear && passInvoiceType;
    });
  }, [invoices, searchTerm, selectedPO, selectedVendor, selectedYear, selectedInvoiceType]);

  const monthlyChartYear = useMemo(
    () => selectedYear || String(new Date().getFullYear()),
    [selectedYear]
  );

  const monthlyChartInvoices = useMemo(() => {
    return filteredInvoices.filter((inv) => {
      const createdAt = inv?.created_at ? new Date(inv.created_at) : null;
      if (!createdAt || !Number.isFinite(createdAt.getTime())) {
        return false;
      }
      return String(createdAt.getFullYear()) === monthlyChartYear;
    });
  }, [filteredInvoices, monthlyChartYear]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    if (v.trim() !== '') {
      setSelectedPO('');
      setSelectedVendor('');
      setSelectedYear('');
      setSelectedInvoiceType('');
    }
  };

  const handlePoFilterChange = (e) => {
    setSelectedPO(e.target.value);
    setSearchTerm('');
  };

  const handleVendorFilterChange = (e) => {
    setSelectedVendor(e.target.value);
    setSearchTerm('');
  };

  const handleYearFilterChange = (e) => {
    setSelectedYear(e.target.value);
    setSearchTerm('');
  };

  const handleInvoiceTypeFilterChange = (e) => {
    setSelectedInvoiceType(e.target.value);
    setSearchTerm('');
  };

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    Boolean(selectedPO || selectedVendor || selectedYear || selectedInvoiceType);

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setSelectedPO('');
    setSelectedVendor('');
    setSelectedYear('');
    setSelectedInvoiceType('');
  };

  const activeFilterChipsSx = {
    height: 28,
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '8px',
    '& .MuiChip-label': {
      px: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const withColor = (arr) =>
    arr.map((item, idx) => ({ ...item, color: PIE_COLORS[idx % PIE_COLORS.length] }));

  const vendorData = useMemo(() => {
    const counts = new Map();
    filteredInvoices.forEach((inv) => {
      const vendor = String(inv?.vendor || '').trim() || 'N/A';
      counts.set(vendor, (counts.get(vendor) || 0) + 1);
    });
    return withColor(
      [...counts.entries()]
        .map(([name, count]) => ({ name, value: count }))
        .sort((a, b) => b.value - a.value)
    );
  }, [filteredInvoices]);

  const statusData = useMemo(() => {
    const counts = new Map();
    filteredInvoices.forEach((inv) => {
      const raw = normalizeStatusKey(inv?.status);
      const label = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unknown';
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return (
      [...counts.entries()]
        // Keep raw counts as `value` so Recharts percent matches legend percent.
        .map(([name, count]) => ({ name, value: count, color: getStatusColor(name) }))
        .sort((a, b) => b.value - a.value)
    );
  }, [filteredInvoices]);

  const invoiceTypeData = useMemo(() => {
    const counts = { FTTS: 0, NON_FTTS: 0, OTHER: 0, UNKNOWN: 0 };
    filteredInvoices.forEach((inv) => {
      const key = resolveInvoiceTypeKey(inv);
      counts[key] += 1;
    });

    return INVOICE_TYPE_CHART_META.map(({ key, label }) => ({
      name: label,
      normalized: key,
      value: counts[key],
      color: INVOICE_TYPE_COLORS[key],
      fill: INVOICE_TYPE_GRADIENTS[key],
    }));
  }, [filteredInvoices]);

  const invoiceTypeChartSlices = useMemo(
    () => invoiceTypeData.filter((entry) => entry.value > 0),
    [invoiceTypeData]
  );

  useEffect(() => {
    const unknownInvoices = filteredInvoices.filter(
      (inv) => resolveInvoiceTypeKey(inv) === 'UNKNOWN'
    );
    const otherInvoices = filteredInvoices.filter((inv) => resolveInvoiceTypeKey(inv) === 'OTHER');

    if (unknownInvoices.length > 0) {
      console.group(`[PO Dashboard] Unknown invoices (${unknownInvoices.length})`);
      console.log('All unknown invoices:', unknownInvoices);
      unknownInvoices.forEach((inv, index) => {
        console.log(`Unknown invoice #${index + 1}`, inv);
      });
      console.groupEnd();
    }

    if (otherInvoices.length > 0) {
      console.group(`[PO Dashboard] Other invoices (${otherInvoices.length})`);
      console.log('All other invoices:', otherInvoices);
      otherInvoices.forEach((inv, index) => {
        console.log(`Other invoice #${index + 1}`, inv);
      });
      console.groupEnd();
    }
  }, [filteredInvoices]);

  const yearlyPOsData = useMemo(() => {
    const counts = new Map();
    filteredInvoices.forEach((inv) => {
      const dt = inv?.created_at ? new Date(inv.created_at) : null;
      const year = dt && Number.isFinite(dt.getTime()) ? String(dt.getFullYear()) : 'Unknown';
      counts.set(year, (counts.get(year) || 0) + 1);
    });
    return withColor(
      [...counts.entries()]
        // Keep raw counts as `value` so Recharts percent matches legend percent.
        .map(([name, count]) => ({ name, value: count }))
        .sort((a, b) => Number(b.name) - Number(a.name))
    );
  }, [filteredInvoices]);

  const monthlyAverageInvoiceData = useMemo(() => {
    const sums = new Array(12).fill(0);
    const counts = new Array(12).fill(0);
    monthlyChartInvoices.forEach((inv) => {
      const dt = inv?.created_at ? new Date(inv.created_at) : null;
      if (!dt || !Number.isFinite(dt.getTime())) {
        return;
      }
      const idx = dt.getMonth();
      sums[idx] += Number(inv?.invoice_amount || 0);
      counts[idx] += 1;
    });
    return MONTHS.map((name, idx) => ({
      name,
      value: counts[idx] ? Number((sums[idx] / counts[idx]).toFixed(2)) : 0,
    }));
  }, [monthlyChartInvoices]);

  const topVendorsByAmountData = useMemo(() => {
    const sums = new Map();
    filteredInvoices.forEach((inv) => {
      const vendor = String(inv?.vendor || '').trim() || 'N/A';
      const amount = Number(inv?.invoice_amount || 0);
      sums.set(vendor, (sums.get(vendor) || 0) + amount);
    });
    return [...sums.entries()]
      .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [filteredInvoices]);

  const monthlyStatusTrendData = useMemo(() => {
    const matrix = MONTHS.map((name) => ({
      name,
      pending: 0,
      approved: 0,
      rejected: 0,
      stop: 0,
      reverted: 0,
      other: 0,
    }));
    monthlyChartInvoices.forEach((inv) => {
      const dt = inv?.created_at ? new Date(inv.created_at) : null;
      if (!dt || !Number.isFinite(dt.getTime())) {
        return;
      }
      const month = dt.getMonth();
      const status = String(inv?.status || '')
        .trim()
        .toLowerCase();
      if (status === 'pending') {
        matrix[month].pending += 1;
      } else if (status === 'approved' || status === 'completed') {
        matrix[month].approved += 1;
      } else if (status === 'rejected') {
        matrix[month].rejected += 1;
      } else if (status === 'stop') {
        matrix[month].stop += 1;
      } else if (status === 'reverted') {
        matrix[month].reverted += 1;
      } else {
        matrix[month].other += 1;
      }
    });
    return matrix;
  }, [monthlyChartInvoices]);

  const topPoSummaryRows = useMemo(() => {
    const map = new Map();
    filteredInvoices.forEach((inv) => {
      const poNumber = String(inv?.po_number || '').trim();
      if (!poNumber) {
        return;
      }
      const existing = map.get(poNumber) || {
        poNumber,
        vendor: String(inv?.vendor || '').trim() || 'N/A',
        invoiceCount: 0,
        totalAmount: 0,
      };
      existing.invoiceCount += 1;
      existing.totalAmount += Number(inv?.invoice_amount || 0);
      if (existing.vendor === 'N/A' && String(inv?.vendor || '').trim()) {
        existing.vendor = String(inv.vendor).trim();
      }
      map.set(poNumber, existing);
    });
    return [...map.values()].sort((a, b) => b.totalAmount - a.totalAmount).slice(0, topPoLimit);
  }, [filteredInvoices, topPoLimit]);

  const quickStats = useMemo(() => {
    const uniquePos = new Set();
    const uniqueVendors = new Set();
    let totalInvoicedAmount = 0;
    let approvedInvoiceAmount = 0;
    let pendingInvoiceAmount = 0;
    let rejectedInvoiceAmount = 0;
    const poAmountByPo = new Map();

    const statusNorm = (s) =>
      String(s || '')
        .trim()
        .toLowerCase();
    filteredInvoices.forEach((inv) => {
      const po = String(inv?.po_number || '').trim();
      const vendor = String(inv?.vendor || '').trim();
      if (po) {
        uniquePos.add(po);
      }
      if (vendor) {
        uniqueVendors.add(vendor);
      }
      const invAmt = Number(inv?.invoice_amount || 0);
      totalInvoicedAmount += invAmt;

      const st = statusNorm(inv?.status);
      if (st === 'approved' || st === 'completed') {
        approvedInvoiceAmount += invAmt;
      } else if (st === 'pending' || st === 'pending_rejection') {
        pendingInvoiceAmount += invAmt;
      } else if (st === 'rejected') {
        rejectedInvoiceAmount += invAmt;
      }

      if (po) {
        const poAmt = Number(inv?.po_amount || 0);
        const existing = poAmountByPo.get(po) || 0;
        poAmountByPo.set(po, Math.max(existing, poAmt));
      }
    });
    const totalInvoices = filteredInvoices.length;
    let totalPoAmount = 0;
    for (const v of poAmountByPo.values()) {
      totalPoAmount += Number(v || 0);
    }
    const remainingAmount = Math.max(0, totalPoAmount - totalInvoicedAmount);

    return [
      { label: 'Total Invoices', value: totalInvoices.toLocaleString(), color: '#2563EB' }, // blue
      { label: 'Unique POs', value: uniquePos.size.toLocaleString(), color: '#CA8A04' }, // yellow
      { label: 'Vendors', value: uniqueVendors.size.toLocaleString(), color: '#0F766E' }, // teal
      {
        label: 'Total PO Amount (PKR)',
        value: Math.round(totalPoAmount).toLocaleString(),
        color: '#7C3AED', // purple
      },
      {
        label: 'Total Invoiced Amount (PKR)',
        value: Math.round(totalInvoicedAmount).toLocaleString(),
        color: '#C2410C', // orange
      },
      {
        label: 'Approved Invoice Amount (PKR)',
        value: Math.round(approvedInvoiceAmount).toLocaleString(),
        color: STATUS_COLORS.approved,
      },
      {
        label: 'Pending Amount (PKR)',
        value: Math.round(pendingInvoiceAmount).toLocaleString(),
        color: STATUS_COLORS.pending,
      },
      {
        label: 'Rejected Amount (PKR)',
        value: Math.round(rejectedInvoiceAmount).toLocaleString(),
        color: STATUS_COLORS.rejected,
      },
      {
        label: 'Remaining (PKR)',
        value: Math.round(remainingAmount).toLocaleString(),
        color: REMAINING_COLOR,
      },
    ];
  }, [filteredInvoices]);

  const amountBreakdownData = useMemo(() => {
    const toNum = (label) => {
      const x = quickStats.find((s) => s.label === label);
      const raw = String(x?.value || '').replace(/[^0-9.-]/g, '');
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };
    const approved = toNum('Approved Invoice Amount (PKR)');
    const pending = toNum('Pending Amount (PKR)');
    const rejected = toNum('Rejected Amount (PKR)');
    const remaining = toNum('Remaining (PKR)');
    return [
      { name: 'Approved', value: approved, color: STATUS_COLORS.approved },
      { name: 'Pending', value: pending, color: STATUS_COLORS.pending },
      { name: 'Rejected', value: rejected, color: STATUS_COLORS.rejected },
      { name: 'Remaining', value: remaining, color: REMAINING_COLOR },
    ].filter((x) => x.value > 0);
  }, [quickStats]);

  const vendorApprovedPendingAmountData = useMemo(() => {
    const map = new Map();
    const statusNorm = (s) =>
      String(s || '')
        .trim()
        .toLowerCase();

    filteredInvoices.forEach((inv) => {
      const vendor = String(inv?.vendor || '').trim() || 'Unknown';
      const amt = Number(inv?.invoice_amount || 0);
      const st = statusNorm(inv?.status);
      const row = map.get(vendor) || { vendor, approved: 0, pending: 0, other: 0, total: 0 };
      if (st === 'approved' || st === 'completed') {
        row.approved += amt;
      } else if (st === 'pending' || st === 'pending_rejection') {
        row.pending += amt;
      } else {
        row.other += amt;
      }
      row.total += amt;
      map.set(vendor, row);
    });

    return [...map.values()]
      .map((r) => ({
        vendor: r.vendor,
        approved: Math.round(r.approved),
        pending: Math.round(r.pending),
        other: Math.round(r.other),
        total: Math.round(r.total),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 12);
  }, [filteredInvoices]);

  useEffect(() => {
    if (!jitterScatterChartRef.current) {
      return;
    }
    const chart = echarts.init(jitterScatterChartRef.current);

    const grid = { left: 80, right: 50, top: 40, bottom: 45 };
    const months = MONTHS;

    const buildOption = () => {
      const width = chart.getWidth() - grid.left - grid.right;
      const jitter = (width / 12) * 0.8;

      const data = [];
      // Build scatter points from invoices: X = month, Y = invoice amount (PKR), Color = amount
      const list = Array.isArray(monthlyChartInvoices) ? monthlyChartInvoices : [];
      const maxPoints = 2500;
      let maxAmt = 0;
      for (let i = 0; i < list.length && data.length < maxPoints; i++) {
        const inv = list[i];
        const dt = inv?.created_at ? new Date(inv.created_at) : null;
        if (!dt || !Number.isFinite(dt.getTime())) {
          continue;
        }
        const amt = Number(inv?.invoice_amount || 0);
        const month = dt.getMonth(); // 0..11
        maxAmt = Math.max(maxAmt, amt);
        data.push([month, amt, amt]);
      }

      const maxRounded = maxAmt > 0 ? Math.ceil(maxAmt / 5) * 5 : 10;

      return {
        title: { show: false },
        grid,
        tooltip: {
          trigger: 'item',
          formatter: (p) => {
            const monthIdx = p?.data?.[0];
            const amt = p?.data?.[2];
            return `${months[monthIdx] || ''}<br/>Amount: ${Number(amt || 0).toLocaleString()} PKR`;
          },
        },
        xAxis: {
          type: 'category',
          jitter,
          data: months,
          axisLabel: { color: chartSubtleTextColor },
          axisLine: { lineStyle: { color: chartAxisColor } },
        },
        yAxis: {
          type: 'value',
          max: maxRounded,
          min: 0,
          axisLabel: {
            color: chartSubtleTextColor,
            formatter: (v) => {
              const n = Number(v || 0);
              if (n >= 1e9) {
                return `${(n / 1e9).toFixed(1)}B`;
              }
              if (n >= 1e6) {
                return `${(n / 1e6).toFixed(1)}M`;
              }
              if (n >= 1e3) {
                return `${(n / 1e3).toFixed(0)}k`;
              }
              return `${n}`;
            },
          },
          splitLine: { lineStyle: { color: chartGridColor } },
        },
        series: [
          {
            name: 'Invoice Amount',
            type: 'scatter',
            data,
            colorBy: 'data',
            symbolSize: 6,
            itemStyle: { opacity: 0.35 },
          },
        ],
      };
    };

    chart.setOption(buildOption(), true);

    const onResize = () => {
      chart.resize();
      chart.setOption(buildOption(), false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (!chart.isDisposed()) {
        chart.dispose();
      }
    };
  }, [chartAxisColor, chartGridColor, chartSubtleTextColor, monthlyChartInvoices]);

  // Custom label function to position percentage inside pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (dashboardLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 480,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      ref={dashboardRef}
      className={`${styles['dashboard-container']} ${
        isDarkBackground ? styles['dashboard-container-dark'] : styles['dashboard-container-light']
      } ${isDarkBackground ? styles['dashboard-charts-dark'] : ''} ${
        isFullscreen ? styles['dashboard-container-fullscreen'] : ''
      }`}
    >
      <Box sx={{ mb: 2 }}>
        {/* Search and Filters Row */}
        <div
          className={`${styles['search-filters-row']} ${
            isDarkBackground ? styles['search-filters-row-dark'] : ''
          }`}
        >
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            autoComplete="off"
            sx={getSearchFieldSx(isDarkBackground, searchTerm.trim() !== '')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    style={{
                      color: isDarkBackground
                        ? searchTerm.trim()
                          ? '#ffd5de'
                          : 'rgba(255, 220, 230, 0.78)'
                        : '#999',
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* PO Filter */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedPO}
                onChange={handlePoFilterChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={getSelectFieldSx(isDarkBackground, Boolean(selectedPO), {
                  lightSelectedBg: '#eff6ff',
                  lightSelectedBorder: '#93c5fd',
                  lightSelectedColor: '#1d4ed8',
                  darkSelectedBorder: 'rgba(147, 197, 253, 0.72)',
                  darkSelectedColor: '#dbeafe',
                })}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  PO
                </MenuItem>
                {poOptions.map((po, index) => (
                  <MenuItem
                    key={po}
                    value={po}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== poOptions.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {po}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Vendor Filter */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedVendor}
                onChange={handleVendorFilterChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={getSelectFieldSx(isDarkBackground, Boolean(selectedVendor), {
                  lightBg: '#fff',
                  lightSelectedBg: '#f0fdfa',
                  lightSelectedBorder: '#5eead4',
                  lightSelectedColor: '#0f766e',
                  darkSelectedBorder: 'rgba(94, 234, 212, 0.72)',
                  darkSelectedColor: '#ccfbf1',
                })}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  VENDOR
                </MenuItem>
                {vendorOptions.map((vendor, index) => (
                  <MenuItem
                    key={vendor}
                    value={vendor}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== vendorOptions.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {vendor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Year Filter */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedYear}
                onChange={handleYearFilterChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={getSelectFieldSx(isDarkBackground, Boolean(selectedYear), {
                  lightSelectedBg: '#faf5ff',
                  lightSelectedBorder: '#d8b4fe',
                  lightSelectedColor: '#6b21a8',
                  darkSelectedBorder: 'rgba(216, 180, 254, 0.72)',
                  darkSelectedColor: '#f3e8ff',
                })}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  YEAR
                </MenuItem>
                {yearOptions.map((year, index) => (
                  <MenuItem
                    key={year}
                    value={year}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== yearOptions.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Invoice Type Filter */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedInvoiceType}
                onChange={handleInvoiceTypeFilterChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={getSelectFieldSx(isDarkBackground, Boolean(selectedInvoiceType), {
                  lightSelectedBg: '#fff7ed',
                  lightSelectedBorder: '#fdba74',
                  lightSelectedColor: '#c2410c',
                  darkSelectedBorder: 'rgba(253, 186, 116, 0.72)',
                  darkSelectedColor: '#ffedd5',
                })}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  Invoice Type
                </MenuItem>
                {INVOICE_TYPE_OPTIONS.map((option, index) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom:
                        index !== INVOICE_TYPE_OPTIONS.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <MuiTooltip title="Clear all filters">
              <span>
                <IconButton
                  size="small"
                  onClick={handleClearAllFilters}
                  disabled={!hasActiveFilters}
                  aria-label="Clear all filters"
                  sx={getIconButtonSx(isDarkBackground, { disabled: !hasActiveFilters })}
                >
                  <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </MuiTooltip>

            <MuiTooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
              <IconButton
                size="small"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
                sx={getIconButtonSx(isDarkBackground, { active: isFullscreen })}
              >
                {isFullscreen ? (
                  <FullscreenExitOutlinedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <FullscreenOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </MuiTooltip>
          </Box>
        </div>

        {hasActiveFilters && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              rowGap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: isDarkBackground ? 'rgba(255, 232, 238, 0.82)' : '#344054',
                mr: 0.25,
              }}
            >
              Active filters
            </Box>
            {searchTerm.trim() !== '' && (
              <Chip
                size="small"
                label={`Search: ${searchTerm.trim()}`}
                title={searchTerm.trim()}
                onDelete={() => setSearchTerm('')}
                sx={{
                  ...activeFilterChipsSx,
                  maxWidth: 280,
                  ...getFilterChipSx(isDarkBackground, {
                    bg: '#f0f9ff',
                    border: '#7dd3fc',
                    color: '#0369a1',
                  }),
                }}
              />
            )}
            {selectedPO !== '' && (
              <Chip
                size="small"
                label={`PO: ${selectedPO}`}
                title={selectedPO}
                onDelete={() => setSelectedPO('')}
                sx={{
                  ...activeFilterChipsSx,
                  ...getFilterChipSx(isDarkBackground, {
                    bg: '#eff6ff',
                    border: '#93c5fd',
                    color: '#1d4ed8',
                  }),
                }}
              />
            )}
            {selectedVendor !== '' && (
              <Chip
                size="small"
                label={`Vendor: ${selectedVendor}`}
                title={selectedVendor}
                onDelete={() => setSelectedVendor('')}
                sx={{
                  ...activeFilterChipsSx,
                  maxWidth: 320,
                  ...getFilterChipSx(isDarkBackground, {
                    bg: '#f0fdfa',
                    border: '#5eead4',
                    color: '#0f766e',
                  }),
                }}
              />
            )}
            {selectedYear !== '' && (
              <Chip
                size="small"
                label={`Year: ${selectedYear}`}
                onDelete={() => setSelectedYear('')}
                sx={{
                  ...activeFilterChipsSx,
                  ...getFilterChipSx(isDarkBackground, {
                    bg: '#faf5ff',
                    border: '#d8b4fe',
                    color: '#6b21a8',
                  }),
                }}
              />
            )}
            {selectedInvoiceType !== '' && (
              <Chip
                size="small"
                label={`Invoice Type: ${getInvoiceTypeLabel(selectedInvoiceType)}`}
                onDelete={() => setSelectedInvoiceType('')}
                sx={{
                  ...activeFilterChipsSx,
                  ...getFilterChipSx(isDarkBackground, {
                    bg: '#fff7ed',
                    border: '#fdba74',
                    color: '#c2410c',
                  }),
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <div className={styles['quick-stats-grid']}>
        {quickStats.map((stat) => (
          <div
            key={stat.label}
            className={`${styles['quick-stat-card']} ${
              isDarkBackground ? styles['quick-stat-card-dark'] : ''
            }`}
            style={{ '--accent': stat.color }}
          >
            <div className={styles['quick-stat-top']}>
              <div className={styles['quick-stat-label']}>{stat.label}</div>
              <div className={styles['quick-stat-badge']} />
            </div>
            <div className={styles['quick-stat-value']}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Row 1: Vendor, Yearly POs, Invoice Status */}
      <div className={`${styles['charts-row']} ${styles['charts-row-pie']}`}>
        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Vendor</h3>
          <div className={styles['pie-chart-wrapper']}>
            <div className={styles['pie-chart-canvas']}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vendorData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius="72%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {vendorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles['pie-legend']}>
              {vendorData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']} title={entry.name}>
                    {entry.name}
                  </span>
                  <span className={styles['legend-value']}>
                    {filteredInvoices.length
                      ? `${((Number(entry.value || 0) * 100) / filteredInvoices.length).toFixed(
                          1
                        )}%`
                      : '0.0%'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Yearly invoices</h3>
          <div className={styles['pie-chart-wrapper']}>
            <div className={styles['pie-chart-canvas']}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={yearlyPOsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius="72%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {yearlyPOsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles['pie-legend']}>
              {yearlyPOsData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']}>{entry.name}</span>
                  <span className={styles['legend-value']}>
                    {filteredInvoices.length
                      ? `${Math.round((Number(entry.value || 0) * 100) / filteredInvoices.length)}%`
                      : '0%'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Invoice Status</h3>
          <div className={styles['pie-chart-wrapper']}>
            <div className={styles['pie-chart-canvas']}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius="72%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles['pie-legend']}>
              {statusData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']}>{entry.name}</span>
                  <span className={styles['legend-value']}>
                    {filteredInvoices.length
                      ? `${Math.round((Number(entry.value || 0) * 100) / filteredInvoices.length)}%`
                      : '0%'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Monthly Status Distribution + Amount Breakdown */}
      <div className={`${styles['charts-row']} ${styles['charts-row-duo']}`}>
        <div className={styles['chart-container']}>
          <h3 className={styles['chart-title']}>Monthly Status Distribution {monthlyChartYear}</h3>
          <BarResponsiveContainer
            width="100%"
            height={300}
            style={{ outline: 'none' }}
            onClick={(e) => e.preventDefault()}
            onFocus={(e) => e.target.blur()}
          >
            <BarChart
              data={monthlyStatusTrendData}
              style={{ outline: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              <Legend wrapperStyle={{ color: chartTextColor }} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <Tooltip content={<CustomCountTooltip />} cursor={false} />
              <Bar dataKey="approved" stackId="a" fill="#10B981" barSize={18} />
              <Bar dataKey="pending" stackId="a" fill="#F59E0B" barSize={18} />
              <Bar dataKey="rejected" stackId="a" fill="#EF4444" barSize={18} />
              <Bar dataKey="stop" stackId="a" fill="#3B82F6" barSize={18} />
            </BarChart>
          </BarResponsiveContainer>
        </div>

        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Amount Breakdown</h3>
          <div className={styles['pie-chart-wrapper']}>
            <div className={styles['pie-chart-canvas']}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={amountBreakdownData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius="72%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {amountBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomAmountTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles['pie-legend']}>
              {amountBreakdownData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']} title={entry.name}>
                    {entry.name}
                  </span>
                  <span className={styles['legend-value']}>
                    Rs {Number(entry.value || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles['charts-row']} ${styles['charts-row-duo']}`}>
        <div className={styles['chart-container']}>
          <h3 className={styles['chart-title']}>Top Vendors (Approved vs Pending Amount)</h3>
          <BarResponsiveContainer width="100%" height={300}>
            <BarChart data={vendorApprovedPendingAmountData}>
              <Legend wrapperStyle={{ color: chartTextColor }} />
              <XAxis
                dataKey="vendor"
                tick={{ fontSize: 11, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tickFormatter={(value) => `Rs ${(Number(value || 0) / 1_000_000).toFixed(1)}M`}
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <Tooltip content={<CustomAmountTooltip />} cursor={false} />
              <Bar dataKey="approved" stackId="a" fill="#10B981" barSize={18} />
              <Bar dataKey="pending" stackId="a" fill="#F59E0B" barSize={18} />
              <Bar dataKey="other" stackId="a" fill="#94A3B8" barSize={18} />
            </BarChart>
          </BarResponsiveContainer>
        </div>

        <div className={styles['chart-container']} style={{ flex: '1 1 50%' }}>
          <h3 className={styles['chart-title']}>Invoice Amount by Month {monthlyChartYear}</h3>
          <div ref={jitterScatterChartRef} className={styles['echarts-box']} />
        </div>
      </div>

      <div className={styles['charts-row']}>
        <div className={styles['chart-container']} style={{ flex: '1 1 33.333%' }}>
          <h3 className={styles['chart-title']}>
            Monthly Average Invoice Amount {monthlyChartYear}
          </h3>
          <BarResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyAverageInvoiceData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGridColor} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <YAxis
                tickFormatter={(value) => `Rs ${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <Tooltip content={<CustomAmountTooltip />} cursor={false} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#8B5CF6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </BarResponsiveContainer>
        </div>

        <div className={styles['chart-container']} style={{ flex: '1 1 33.333%' }}>
          <h3 className={styles['chart-title']}>Top Vendors by Amount</h3>
          <BarResponsiveContainer width="100%" height={300}>
            <BarChart data={topVendorsByAmountData}>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <YAxis
                tickFormatter={(value) => `Rs ${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: chartSubtleTextColor }}
                axisLine={{ stroke: chartAxisColor }}
              />
              <Tooltip content={<CustomAmountTooltip />} cursor={false} />
              <Bar dataKey="value" fill="#06B6D4" barSize={16} />
            </BarChart>
          </BarResponsiveContainer>
        </div>

        <div className={styles['pie-chart-container']} style={{ flex: '1 1 33.333%' }}>
          <h3 className={styles['chart-title']}>Invoice Type</h3>
          <div className={styles['invoice-type-chart-layout']}>
            <div className={styles['invoice-type-chart-shell']}>
              {invoiceTypeChartSlices.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <defs>
                      <linearGradient id="invoiceTypeFttsGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#60A5FA" />
                        <stop offset="55%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#1D4ED8" />
                      </linearGradient>
                      <linearGradient id="invoiceTypeNonFttsGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#FCD34D" />
                        <stop offset="55%" stopColor="#F97316" />
                        <stop offset="100%" stopColor="#C2410C" />
                      </linearGradient>
                      <linearGradient id="invoiceTypeOtherGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#C4B5FD" />
                        <stop offset="55%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#6D28D9" />
                      </linearGradient>
                      <linearGradient id="invoiceTypeUnknownGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#94A3B8" />
                        <stop offset="55%" stopColor="#64748B" />
                        <stop offset="100%" stopColor="#334155" />
                      </linearGradient>
                    </defs>
                    <Pie
                      data={invoiceTypeChartSlices}
                      cx="50%"
                      cy="50%"
                      innerRadius="62%"
                      outerRadius="88%"
                      paddingAngle={invoiceTypeChartSlices.length > 1 ? 4 : 0}
                      cornerRadius={8}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                      onMouseEnter={(_, index) => setActiveInvoiceTypeIndex(index)}
                      onMouseLeave={() => setActiveInvoiceTypeIndex(null)}
                      isAnimationActive
                      animationDuration={700}
                    >
                      {invoiceTypeChartSlices.map((entry, index) => (
                        <Cell
                          key={`invoice-type-cell-${entry.normalized}`}
                          fill={entry.fill}
                          opacity={
                            activeInvoiceTypeIndex === null || activeInvoiceTypeIndex === index
                              ? 1
                              : 0.5
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={<InvoiceTypeChartTooltip total={filteredInvoices.length} />}
                      cursor={false}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles['invoice-type-chart-empty']}>
                  <div className={styles['invoice-type-chart-empty-ring']} />
                </div>
              )}
              <div className={styles['invoice-type-chart-center']}>
                <span className={styles['invoice-type-chart-center-label']}>Total</span>
                <span className={styles['invoice-type-chart-center-value']}>
                  {filteredInvoices.length.toLocaleString()}
                </span>
              </div>
            </div>

            <div className={styles['invoice-type-legend']}>
              {invoiceTypeChartSlices.length > 0 ? (
                invoiceTypeChartSlices.map((entry) => {
                  const percent = filteredInvoices.length
                    ? ((Number(entry.value || 0) * 100) / filteredInvoices.length).toFixed(1)
                    : '0.0';
                  const sliceIndex = invoiceTypeChartSlices.findIndex(
                    (slice) => slice.normalized === entry.normalized
                  );
                  return (
                    <div
                      key={entry.normalized}
                      className={`${styles['invoice-type-legend-card']} ${
                        activeInvoiceTypeIndex === sliceIndex
                          ? styles['invoice-type-legend-card-active']
                          : ''
                      }`}
                      onMouseEnter={() => setActiveInvoiceTypeIndex(sliceIndex)}
                      onMouseLeave={() => setActiveInvoiceTypeIndex(null)}
                    >
                      <div className={styles['invoice-type-legend-top']}>
                        <span
                          className={styles['invoice-type-legend-swatch']}
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className={styles['invoice-type-legend-name']}>{entry.name}</span>
                        <span className={styles['invoice-type-legend-percent']}>{percent}%</span>
                      </div>
                      <div className={styles['invoice-type-legend-metrics']}>
                        <span className={styles['invoice-type-legend-count']}>
                          {Number(entry.value || 0).toLocaleString()}
                        </span>
                        <div className={styles['invoice-type-legend-bar']}>
                          <span
                            className={styles['invoice-type-legend-bar-fill']}
                            style={{ width: `${percent}%`, backgroundColor: entry.color }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles['invoice-type-legend-empty']}>
                  No invoice type data available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PO Summary table — hidden on PO Workflow dashboard (showPoSummary=false) */}
      {showPoSummary && (
        <div className={styles['chart-container']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <h3 className={styles['chart-title']} style={{ margin: 0 }}>
              PO Summary
            </h3>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTopPoLimit((v) => (v === 10 ? 50 : 10))}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                borderColor: '#d0d5dd',
                color: '#344054',
                '&:hover': { borderColor: '#98a2b3', backgroundColor: '#f9fafb' },
              }}
            >
              {topPoLimit === 10 ? 'Show more' : 'Show less'}
            </Button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles['dashboard-summary-table']}>
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Vendor</th>
                  <th>Invoices</th>
                  <th>Total Invoice Amount</th>
                </tr>
              </thead>
              <tbody>
                {topPoSummaryRows.length > 0 ? (
                  topPoSummaryRows.map((row) => (
                    <tr key={row.poNumber}>
                      <td>{row.poNumber}</td>
                      <td>{row.vendor}</td>
                      <td>{row.invoiceCount}</td>
                      <td>Rs {row.totalAmount.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: '#667085' }}>
                      No data available for selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
